import { useState, useEffect } from 'react'
import en from './locales/en'
import ko from './locales/ko'
import ja from './locales/ja'
import zh from './locales/zh'
import es from './locales/es'
import fr from './locales/fr'
import de from './locales/de'
import pt from './locales/pt'
import ru from './locales/ru'
import { sendRevenueEvent, type EasyAirScreenRevenueEvent } from './revenueFunnel'
import { isLanguage, resolveInitialLanguage } from './lib/language'

const translations: Record<string, typeof en> = { en, ko, ja, zh, es, fr, de, pt, ru }

const PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.somnal.app.easy.air.screen'

/**
 * App Store 판매 링크. **2026-08-06 부터 팔리고 있었는데 이 랜딩에는 링크가 없었다** —
 * iOS 로 들어온 방문자는 살 길이 없어 전원 이탈했다(2026-08-11 발각).
 * 국가 코드를 빼면 애플이 방문자 지역으로 보내 준다.
 */
const APP_STORE_URL = 'https://apps.apple.com/app/id6757753606'

/**
 * 데스크톱 설치 파일. 이름은 `electron-builder.yml` 의 productName 에서 공백을 뺀 것이고
 * (`Easy Air Screen` → `EasyAirScreen`), `release-desktop.sh` 가 이 이름으로 서버에 올린다.
 * 용량은 실제 업로드본 기준 — 모바일 데이터로 받는 사람이 있어 미리 알려 준다.
 *
 * macOS 하나뿐이다 — Windows 는 2026-08-10 지원 대상에서 뺐다(사용자 결정). Windows 쪽
 * 가상 디스플레이 드라이버(parsec-vdd)가 원본 저장소 시절부터 설치 파일에 동봉된 적이 없어
 * 수동 설치 안내로만 돌던 상태였고, 제품 약속을 지킬 수 있는 플랫폼만 남겼다.
 */
const DESKTOP_DOWNLOAD = {
  href: '/downloads/EasyAirScreen.dmg',
  size: '179MB',
  event: 'desktop_download_macos',
} as const satisfies { href: string; size: string; event: EasyAirScreenRevenueEvent }

export function syncDocumentLanguage(language: string): string {
  const resolved = isLanguage(language) ? language : 'en'
  document.documentElement.lang = resolved
  return resolved
}

export default function Page() {
  /**
   * 언어를 **첫 렌더에 바로** 정한다. 예전에는 `useState('en')` + `mounted` 플래그로 두어
   * 첫 페인트에 `null` 을 그렸는데, 그러면 JS 를 안 돌리는 크롤러·미리보기 봇이 **빈 문서**를
   * 본다. SSR 이 없는 순수 CSR 이라 하이드레이션 불일치를 걱정할 이유도 없었다 —
   * Next 에서 옮겨 올 때 같이 딸려 온 방어였다.
   */
  const [currentLang, setCurrentLang] = useState<string>(resolveInitialLanguage)

  useEffect(() => {
    syncDocumentLanguage(currentLang)
  }, [currentLang])

  useEffect(() => {
    // Navbar 의 언어 선택기가 바꾸면 'languagechange' 커스텀 이벤트로 동기화
    const onChange = (e: CustomEvent<string>) => setCurrentLang(syncDocumentLanguage(e.detail))
    window.addEventListener('languagechange', onChange as EventListener)
    return () => window.removeEventListener('languagechange', onChange as EventListener)
  }, [])

  const t = translations[currentLang] ?? en

  return (
    <>
      {/* 2단 전환은 lg 부터다 — 768(아이패드)에서 나누면 글줄이 좁아져 제목이 세 줄로 깨진다 */}
      <section className="mx-auto grid max-w-5xl items-center gap-12 px-5 pt-12 pb-16 sm:px-6 md:pt-20 md:pb-24 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
        <div className="min-w-0">
          <h1 className="text-[2rem] leading-[1.18] font-bold tracking-tight text-balance md:text-[2.75rem]">
            {t.subtitle}
          </h1>
          <p className="text-ink-mut mt-5 text-lg leading-8">{t.description}</p>

          {/*
            받는 자리는 여기 하나다 — 아래에 같은 링크를 한 벌 더 두지 않는다.
            순서는 **Mac 먼저, 그다음 기기 앱**이다. 이 랜딩을 보는 사람은 대개
            Mac 브라우저 앞에 앉아 있고, 지금 이 기기에서 바로 할 수 있는 일이
            Mac 앱 설치뿐이다. 기기 앱은 결국 태블릿·아이패드에서 스토어를 열어야 하니
            여기서는 "그건 저쪽 기기에서" 라고 알려 주는 두 번째 줄에 둔다.

            그래서 Mac 만 브랜드색 채움이고 스토어 줄은 테두리다. 구매가 일어나는 곳은
            **스토어 두 곳**(Play=Android 태블릿, App Store=iPhone·iPad)이라 유료 링크가
            덜 눈에 띄는 배치이긴 한데, PC 앱 없이는 산 앱이 아무 일도 못 한다 —
            무료 앱이 유료 앱의 선행 조건이라 앞에 세운다.
            구분선 문구는 이미 9개 언어에 다 있는 `downloadMobile` 을 그대로 쓴다.
          */}
          <div className="mt-9 max-w-md">
            <a
              href={DESKTOP_DOWNLOAD.href}
              onClick={() => sendRevenueEvent(DESKTOP_DOWNLOAD.event)}
              className="bg-brand text-on-brand hover:bg-brand-strong flex h-[56px] w-full items-center justify-center gap-2 rounded-xl px-5 text-center text-[15px] font-semibold transition-colors sm:px-6"
            >
              {t.downloadMacOS}
              {/* 용량을 버튼 안에 붙여 둔다 — 모바일 데이터로 179MB 를 모르고 받는 일이 없게 */}
              <span className="text-[13px] font-normal tabular-nums opacity-75">
                {DESKTOP_DOWNLOAD.size}
              </span>
            </a>

            {/* 선 두 토막 사이에 글자를 끼운 구분선. 선은 장식이라 스크린리더에서 감춘다 */}
            <div className="my-6 flex items-center gap-3">
              <span aria-hidden="true" className="bg-hair h-px flex-1" />
              <span className="text-ink-mut text-[13px] font-medium">{t.downloadMobile}</span>
              <span aria-hidden="true" className="bg-hair h-px flex-1" />
            </div>

            {/*
              두 스토어 다 공식 배지다. 배지 그림에 스토어 이름이 박혀 있어서 읽지 않아도
              알아본다 — 예전에 App Store 만 글자 버튼이었는데, 배지 옆에 선 글자 버튼은
              "이건 스토어가 아닌가?" 를 만든다.

              **높이가 서로 다른 건 실수가 아니다.** Play 배지 PNG 에는 여백이 그려져 있고
              (그림이 실제로 차 있는 영역은 높이의 67%) Apple SVG 에는 없다. 같은 height 를
              주면 Play 가 작아 보인다. 근거와 실측값은
              `frontend/shared/design/assets/store/README.md` 에 있다.
            */}
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendRevenueEvent('store_click_google_play')}
                className="-m-[9px] block transition-opacity hover:opacity-85"
              >
                {/*
                  alt 는 스토어 이름만 — 배지에 박힌 문구를 옮겨 적으면 스크린리더가 두 번
                  읽는다. 고유명사라 번역 대상도 아니다.

                  감싼 `<a>` 의 `-m-[9px]` 은 **그림이 아니라 레이아웃 상자만** 줄인다. 56px 로
                  그리면 PNG 안에 그려진 투명 여백이 사방 9px(41/250×56)이라, 그대로 두면
                  검은 사각형이 위 macOS 버튼보다 9px 안으로 들어가 줄이 어긋나 보이고
                  배지 사이 간격도 12px 이 아니라 21px 이 된다. 그림은 손대지 않는다 —
                  가이드라인이 요구하는 여백은 `gap-3` 과 주변 빈 공간이 준다.

                  **음수 여백은 `<img>` 가 아니라 `<a>` 에 준다.** 그림에 직접 주면 Tailwind
                  preflight 의 `img { max-width: 100% }` 와 물려서 폭만 18px 깎이고 높이는
                  그대로라 배지가 옆으로 눌린다(실측: 144.7→126.7).
                */}
                <img
                  src="/images/google-play-badge.png"
                  alt="Google Play"
                  width={182}
                  height={70}
                  className="h-[56px] w-auto max-w-none"
                />
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendRevenueEvent('store_click_app_store')}
                className="transition-opacity hover:opacity-85"
              >
                <img
                  src="/images/app-store-badge.svg"
                  alt="App Store"
                  width={126}
                  height={42}
                  className="h-[42px] w-auto"
                />
              </a>
            </div>
          </div>

          <p className="text-ink-mut mt-6 text-sm leading-6">
            <strong className="text-ink font-semibold">{t.purchasePromise}</strong>{' '}
            {t.companionPromise}
          </p>
        </div>

        {/*
          추상 일러스트 대신 실제 사용 장면을 둔다 — 이 제품이 파는 것은 "노트북 옆에 화면이
          하나 더 생긴다"이고, 그건 글보다 그림이 빠르다. 두 화면을 가로지르는 곡선이
          "한 바탕화면이 이어진다"를 말한다. 화면 속 UI 는 추상 블록이라 특정 앱을 사칭하지 않는다.
          webp 우선 + jpg 대체. 첫 화면 이미지라 lazy 로 미루지 않는다.
        */}
        <picture className="w-full min-w-0">
          <source srcSet="/images/hero-desk.webp" type="image/webp" />
          <img
            src="/images/hero-desk.jpg"
            alt={t.subtitle}
            width={1280}
            height={853}
            fetchPriority="high"
            className="border-hair w-full rounded-2xl border"
          />
        </picture>
      </section>

      <section className="border-hair bg-surface border-y">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 py-16 md:py-20">
          <h2 className="text-2xl font-bold tracking-tight">{t.quickSetup}</h2>
          <ol className="mt-8 grid gap-x-8 gap-y-7 sm:grid-cols-2">
            {t.setupSteps.map((step, i) => (
              <li key={step} className="flex gap-4">
                <span className="bg-brand text-on-brand flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
                  {i + 1}
                </span>
                <span className="pt-1 leading-7">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-hair bg-surface border-t">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 py-16 md:py-20">
          <div className="bg-brand-tint border-brand-soft/40 rounded-2xl border p-7 md:p-9">
            <h2 className="text-brand text-lg font-bold tracking-tight">{t.performanceTip}</h2>
            <p className="text-ink mt-2 leading-7">{t.performanceDescription}</p>
          </div>
        </div>
      </section>

      {/*
        FAQ. 방문자용이기도 하지만 **기계가 읽는 자리이기도 하다** —
        2026 기준 앱 설치의 절반 이하만 스토어 검색에서 오고, LLM 이 앱을 추천할 때 보는 것은
        스토어 랭킹이 아니라 웹의 FAQ·비교 서술이다 (docs/app-marketing-playbook.html §순풍).
        그래서 **접는 UI 를 쓰지 않는다** — `<details>` 로 감추면 사람에게도 한 번 더 클릭이고,
        요약만 읽는 크롤러에겐 답변이 덜 잡힌다. 전부 펼친 평문으로 둔다.

        같은 내용의 `FAQPage` 구조화 데이터는 `index.html` 정적 셸에 있다. 이 사이트는 CSR 이라
        JS 를 돌리지 않는 크롤러는 여기 마크업을 못 본다 — 셸에 넣어야 그쪽에도 닿는다.
        셸의 블록은 **한국어 원본 한 벌**이고, `scripts/build-locales.ts` 가 `dist/<lang>/` 을
        만들 때 그 언어의 문답으로 갈아 끼운다. 그래서 문답의 출처는 여기 `locales/*.ts` 하나다.
      */}
      <section className="border-hair border-t">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 py-16 md:py-20">
          <h2 className="text-2xl font-bold tracking-tight">{t.faqTitle}</h2>
          <dl className="mt-9 grid gap-x-12 gap-y-8 md:grid-cols-2">
            {t.faq.map((item) => (
              <div key={item.q} className="min-w-0">
                <dt className="text-[15px] leading-7 font-semibold">{item.q}</dt>
                <dd className="text-ink-mut mt-1.5 ml-0 leading-7">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  )
}
